import { Paper, Stack, Text } from '@mantine/core';
import { AvatarMobile } from 'components/AvatarMobile';
import { FC } from 'react';
import { ReactComponent as IconCalendar } from './img/iconCalendar.svg';
import { ReactComponent as IconOferta } from './img/iconOferta.svg';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconTime } from './img/iconTime.svg';
import { ReactComponent as IconReject } from './img/iconReject.svg';
import { ReactComponent as IconCancel } from './img/iconCancel.svg';
import { ReactComponent as IconComment } from './img/iconCommentGray.svg';
import { ReactComponent as IconDoneBlue } from './img/iconDoneBlue.svg';
import { ReactComponent as IconRuble } from './img/iconRuble.svg';
import { formatCustomDateContract } from 'utils/date';
import { statusText } from 'components/ContractItem';
import { ContractActionsDrawer } from 'components/ContractItem/ContractActionsDrawer';
import { ContractStatus, ContractType } from 'redux/Api/contractsApi/type';
import styles from './ContractsListMobile.module.scss';
import { ReactComponent as IconTochks } from './img/iconTochka.svg';
import { ReactComponent as IconProblem } from '../../assets/img/Employed/iconProblem.svg';
import { useMediaQuery } from '@mantine/hooks';
import { ContractActions } from 'components/ContractItem/ContractActions';
import { FormatAmount } from 'ui/FormatAmount';
import { Nothing } from 'components/Nothing';
import { AppTooltip } from 'ui/AppTooltip';
import { TextWithTooltip } from 'hooks/TextWithTooltip';

interface ContractsListMobileTypes {
  data?: ContractType[];
  intersectionComponent: JSX.Element | null;
  hidePerformer?: boolean;
}

export const statusIcons: Record<ContractStatus, JSX.Element> = {
  signed_by_customer: <IconTime />,
  signed_by_worker: <IconDone />,
  rejected: <IconCancel />,
  revoked: <IconReject />,
  terminated: <IconReject />,
  expired: <IconReject />,
};

export const statusColor: Record<ContractStatus, string> = {
  signed_by_customer: '#121922',
  signed_by_worker: '#28A879',
  rejected: '#E10D0D',
  revoked: '#121922',
  terminated: '#121922',
  expired: '#121922',
};

export const ContractsListMobile: FC<ContractsListMobileTypes> = ({
  data,
  intersectionComponent,
  hidePerformer = false,
}) => {
  const isMobile = useMediaQuery(`(width < 768px)`);
  return (
    <Stack align='stretch' justify='center' mb='lg' gap='8px'>
      {data?.length ? (
        data.map((item) => (
          <Paper
            shadow='0'
            style={{
              padding: '12px 16px',
            }}
            radius='8px'
            key={item.id}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                }}>
                <Text fw='500' fz='16px' lh='22px' c='#121922'>
                  № {item.number}
                </Text>
                {isMobile ? (
                  <ContractActionsDrawer
                    contract={item}
                    status={item.status}
                    isResident={item?.worker?.nationality === 'Россия'}
                  />
                ) : (
                  <ContractActions
                    contract={item}
                    status={item.status}
                    isResident={item?.worker?.nationality === 'Россия'}
                  />
                )}
              </div>

              {!hidePerformer && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'nowrap',
                    }}>
                    <AvatarMobile
                      size={32}
                      name={item?.worker?.name}
                      surname={item?.worker?.surname}
                      avatar={item?.worker?.avatar}
                      isDeleted={item?.worker?.is_deleted}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div className={styles.text}>
                        {!item?.worker?.surname
                          ? item?.worker?.name
                          : item?.worker?.patronymic
                          ? `${item?.worker?.surname} ${item?.worker?.name} ${item?.worker?.patronymic}`
                          : `${item?.worker?.surname} ${item?.worker?.name}`}
                      </div>
                      <div className={styles.inn}>
                        ИНН {item?.worker?.inn}
                        <IconTochks />
                        {item?.worker?.nationality}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className={styles.lineBox}>
                <IconCalendar />
                <Text lh='18px' fz='16px' c='#121922'>
                  {item.date_start && formatCustomDateContract(item.date_start)} -{' '}
                  {item.date_end ? formatCustomDateContract(item.date_end) : 'Бессрочно'}
                </Text>
              </div>

              <div className={styles.lineBox}>
                <IconOferta />
                <div className={styles.textOferta}>
                  {item.oferta?.title || 'Единый договор'}
                </div>
              </div>

              <div className={styles.lineBox}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {statusIcons[item.status as ContractStatus]}
                    <Text
                      lh='18px'
                      fw='400'
                      fz='16px'
                      c={statusColor[item.status as ContractStatus]}>
                      {statusText[item.status as ContractStatus]}{' '}
                      {item.status === 'rejected' || item.status === 'terminated'
                        ? formatCustomDateContract(item.status_changed_at)
                        : null}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {!isMobile && item?.worker?.is_deleted && (
                      <div className={styles.lineProblem}>
                        <IconProblem />
                        <TextWithTooltip
                          content='Профиль удален'
                          className={styles.textError}
                          isMobile
                        />
                      </div>
                    )}
                    {!item.is_mvd_notified &&
                      !item.is_mvd_notified_on_termination &&
                      item.comment && (
                        <div style={{ paddingRight: '8px' }}>
                          <CommentTooltip comment={item.comment}>
                            <IconComment style={{ cursor: 'pointer' }} />
                          </CommentTooltip>
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {item.is_mvd_notified && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                    height: '20px',
                  }}>
                  <div style={{ display: 'flex', margin: '8px 0', gap: '8px' }}>
                    <IconDoneBlue />
                    <Text lh='18px' fz='16px' c='#002CFB'>
                      {'Уведомил МВД о заключении'}
                    </Text>
                  </div>
                  {item.is_mvd_notified &&
                    !item.is_mvd_notified_on_termination &&
                    item.comment && (
                      <div style={{ paddingRight: '8px' }}>
                        <CommentTooltip comment={item.comment}>
                          <IconComment style={{ cursor: 'pointer' }} />
                        </CommentTooltip>
                      </div>
                    )}
                </div>
              )}

              {item.is_mvd_notified_on_termination && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                    height: '20px',
                  }}>
                  <div style={{ display: 'flex', margin: '8px 0', gap: '8px' }}>
                    <IconDoneBlue />
                    <Text lh='18px' fz='16px' c='#002CFB'>
                      {'Уведомил МВД о расторжении'}
                    </Text>
                  </div>
                  {item.is_mvd_notified_on_termination && item.comment && (
                    <div style={{ paddingRight: '8px' }}>
                      <CommentTooltip comment={item.comment}>
                        <IconComment style={{ cursor: 'pointer' }} />
                      </CommentTooltip>
                    </div>
                  )}
                </div>
              )}

              {isMobile && item?.worker?.is_deleted && (
                <div className={styles.lineProblem} style={{ justifyContent: 'start' }}>
                  <IconProblem />
                  <TextWithTooltip
                    content='Профиль удален'
                    className={styles.textError}
                    isMobile
                  />
                </div>
              )}
            </div>
          </Paper>
        ))
      ) : (
        <Nothing style={{ height: '320px' }} title='Ничего не найдено' />
      )}
      {intersectionComponent}
    </Stack>
  );
};

export const CommentTooltip = ({
  comment,
  children,
}: {
  comment?: string;
  children: React.ReactNode;
}) => {
  if (!comment) return <>{children}</>;

  return (
    <AppTooltip position='top-end' label={comment}>
      {children}
    </AppTooltip>
  );
};
